/**
 * Утилиты для работы с историей заказов в ресторане
 */

export interface RestaurantOrder {
  orderId: string;
  date: string; // ISO string
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
  total: number;
  deliveryType: 'beach_club' | 'panorama' | 'pickup' | 'delivery';
  paymentType: 'cash' | 'card';
  bonusUsed: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
}

const STORAGE_KEY = 'restaurant-orders-history';

/**
 * Получить историю заказов из localStorage
 */
export function getRestaurantOrders(): RestaurantOrder[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const orders = JSON.parse(stored);
    // Сортируем по дате (новые первыми)
    return orders.sort((a: RestaurantOrder, b: RestaurantOrder) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  } catch (error) {
    console.error('Error loading restaurant orders:', error);
    return [];
  }
}

/**
 * Сохранить новый заказ в историю
 */
export function saveRestaurantOrder(order: RestaurantOrder): void {
  try {
    const orders = getRestaurantOrders();
    orders.unshift(order); // Добавляем в начало (новый заказ)

    // Ограничиваем историю последними 50 заказами
    const limitedOrders = orders.slice(0, 50);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedOrders));
  } catch (error) {
    console.error('Error saving restaurant order:', error);
  }
}

/**
 * Обновить статус заказа
 */
export function updateOrderStatus(orderId: string, status: RestaurantOrder['status']): void {
  try {
    const orders = getRestaurantOrders();
    const orderIndex = orders.findIndex(o => o.orderId === orderId);

    if (orderIndex !== -1) {
      orders[orderIndex].status = status;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
    }
  } catch (error) {
    console.error('Error updating order status:', error);
  }
}

/**
 * Получить заказ по ID
 */
export function getOrderById(orderId: string): RestaurantOrder | null {
  try {
    const orders = getRestaurantOrders();
    return orders.find(o => o.orderId === orderId) || null;
  } catch (error) {
    console.error('Error getting order by ID:', error);
    return null;
  }
}

/**
 * Очистить историю заказов
 */
export function clearRestaurantOrders(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing restaurant orders:', error);
  }
}
